import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  tier: string;
  wallet_balance: number;
  bonus_balance: number;
  loyalty_points: number;
  referral_code: string | null;
  referred_by: string | null;
  transaction_pin: string | null;
  login_pin: string | null;
  pin_set_at: string | null;
  profit_balance: number;
  earnings_balance: number | null;
  ambassador_referral_count: number;
  created_at: string;
  device_id: string | null;
  avatar_url: string | null;
  welcome_bonus_locked?: number | null;
  welcome_bonus_unlocked?: boolean | null;
  total_funded?: number | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const PUBLIC_ROUTES = [
  "/",
  "/auth",
  "/ai",
  "/chat",
  "/ai-landing",
  "/AI",
  "/artifacts",
  "/pricing",
  "/use-cases",
  "/comparison",
  "/docs",
  "/docs/mcp",
  "/articles",
  "/preview",
  "/ai/pricing",
  "/ai/use-cases",
  "/ai/comparison",
  "/ai/docs",
  "/ai/articles",
  "/ai/preview",
  "/blog",
  "/livescore",
  "/campus",
  "/about",
  "/legal",
  "/legal/terms",
  "/legal/privacy",
  "/legal/refund",
  "/legal/cookie",
  "/legal/complaints",
  "/legal/disclaimer",
  "/download",
  "/play-store",
  "/faq",
  "/support",
  "/leaderboard",
  "/dnpl",
  "/flyer",
  "/marketplace",
  "/voucher",
  "/developer",
  "/rewards",
  "/ambassador",
  "/notifications",
  "/compare",
  "/retailers",
];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (data) {
      setProfile(data as Profile);
    } else if (error || !data) {
      setTimeout(async () => {
        const { data: retry } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle();
        if (retry) setProfile(retry as Profile);
      }, 1000);
    }
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => fetchProfile(session.user.id), 0);
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !session) {
      const isPublic = PUBLIC_ROUTES.some(r => 
        r === "/" 
          ? location.pathname === "/" 
          : location.pathname === r || location.pathname.startsWith(r + "/")
      );
      if (!isPublic) {
        navigate("/auth", { replace: true });
      }
    }
  }, [session, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const isPublic = PUBLIC_ROUTES.some(r => 
    r === "/" 
      ? location.pathname === "/" 
      : location.pathname === r || location.pathname.startsWith(r + "/")
  );
  if (!session && !isPublic) return null;

  return <>{children}</>;
};
